// src/components/Dashboard/CommandDashboardDesktop.jsx
//
// Desktop Command surface — the 3-column re-layout of the mobile loop-home
// (Read → Equip → Deploy → Manage → Review). A top bar (greeting + DeskLoopRail
// + wins chip) over a grid: left IdentityPanel · center act-now spine
// (Read → Equip → Deploy) · right battle lifecycle (Manage + Review). Renders
// behind COMMAND_DASHBOARD_DESKTOP_ENABLED in place of DashboardDesktop, with
// the mobile dashboard's prop bag.
//
// Phase-aware (mirrors mobile): idle shows Deploy (03); a live agent battle
// drops Deploy, locks the Equip bench, and shifts the loop rail to Manage.
//
// The shell owns all state (agent, accent, live/idle, deploy flag) and threads
// `accent` + handlers down; columns never re-derive the accent. ReadColumn and
// EquipBench own their own data hooks (DRB / forge+watchlist) so the brief and
// bench reflect live state without prop-drilling.

import React, { useEffect, useRef, useState } from 'react';
import { Trophy, Activity, Award } from 'lucide-react';
import { CMD, Eyebrow, Mono, SectionLabel } from './commandUI';
import useAgent from '../../hooks/useAgent';
// Mastery P3: the owner profile behind the RecordSheet's mastery cards —
// zero reads and null while MASTERY_SURFACE_ENABLED is false (dark).
import useMasteryProfile from '../../hooks/useMasteryProfile';
import useRecentCompletedAgentBattles from '../../hooks/useRecentCompletedAgentBattles';
import { deployAgent } from '../../services/agentDeploy';
// Command Center repair R1: the one result → ceremony mapping, and the
// ceremony-off path for the agent's already-running battle.
import { deployResultFor, isExistingBattleResult, isOrphanedResult } from './deployResult';
import { openExistingBattleDirectly } from './deployCeremony/existingBattle';
// Record-only ceremony instrumentation (console; no writes, nothing gates on it).
import * as ceremonyTiming from './deployCeremony/ceremonyTiming';
import ManageStation from './ManageStation';
import ReviewStation from './ReviewStation';
import AgentRecordSheet from './AgentRecordSheet';
import DeskLoopRail from './desktop/DeskLoopRail';
import IdentityPanel from './desktop/IdentityPanel';
import ReadColumn from './desktop/ReadColumn';
import EquipBench from './desktop/EquipBench';
import DeployCard from './desktop/DeployCard';
import ScoutingBoardSheet from './ScoutingBoardSheet';
import DeployCeremony from './deployCeremony/DeployCeremony';
// Pilot P1c (founder ruling E1): the due-idea screen, before any deploy call.
import DueIdeaInterstitial from './deployCeremony/DueIdeaInterstitial';
import { checkDueIdea } from '../../services/dueIdeaCheck';
// isStarfieldOn (Delight Layer Task 2, spec V2 D4): this component's root paints
// an OPAQUE CMD.bg over the z0 background slot, so it is what hides the
// starfield. Flag on => transparent and the field shows through; off =>
// byte-identical to today.
import { SCOUTING_BOARD_ENABLED, CASUAL_CLONE_CONCURRENCY_ENABLED, isDeployCeremonyOn, isStarfieldOn } from '../../config/featureFlags';
import { deriveDeployGate, findLiveBaggerBomb, hasLiveBaggerBomb } from '../../utils/commandCenterLiveBattles';
// Command Center Sync (Pass 1): the ONE seam through which this shell sees
// battle state. No field of agentBattles / voiceLayerCache / agents is read
// directly here — that is the DO-NOT line the pass exists to hold (spec §5).
import useCommandCenterSync from '../../hooks/useCommandCenterSync';
import { syncForBattle } from '../../adapters/baggerbombAdapter';
import AgentDesk from './desk/AgentDesk';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// idle empty-state for the right-column lifecycle blocks (Manage / Review)
function IdleBlock({ icon, title, sub }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '15px 16px', borderRadius: 16,
      background: CMD.surface, border: `1px dashed ${CMD.hair2}`, opacity: 0.9,
    }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: CMD.bg, border: `1px solid ${CMD.hair}` }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, color: CMD.ink2, fontWeight: 600 }}>{title}</div>
        <Mono style={{ fontSize: 10.5, color: CMD.ink3, marginTop: 2, display: 'block' }}>{sub}</Mono>
      </div>
    </div>
  );
}

export default function CommandDashboardDesktop({
  user,
  setScreen,
  setShowForge,
  setCurrentBattle,
  activeAgentBattles = [],
  voiceLayerCaches = {},
  onCreateAgentBattle,
  onOpenAgentBattle,
  onEnterBattle,
}) {
  // Resolve the agent by ownerId === odUserId — the same key the mobile
  // CommandDashboard uses.
  const { agent, loading: agentLoading, record, winRate, levelConfig, nextLevelInfo, deployText, activeDirectives } = useAgent(user?.odUserId);
  const masteryProfile = useMasteryProfile(agent?.ownerId || null);

  // The user-picked primaryColor supersedes the Haiku avatarColors.
  const accent = agent?.primaryColor || agent?.avatarColors?.[0] || CMD.teal;
  const agentName = agent?.name || user?.username || 'your agent';
  const wins = agent?.stats?.wins ?? 0;

  // ── Deploy / Manage / Review ──────────────────────────────────────────────
  const liveBattles = (activeAgentBattles || []).filter((b) => b.status === 'active');
  const liveBattle = liveBattles[0] || null;
  const isLive = Boolean(liveBattle);
  // Per-Battle Concurrency (Phase 1.5) — the per-type deploy gate, shared with the
  // mobile shell via deriveDeployGate (identical logic, one source). Flag-OFF every
  // value reduces to the legacy `isLive` gate, byte-identical.
  const concurrencyOn = CASUAL_CLONE_CONCURRENCY_ENABLED;
  const { orderedLiveBattles, deployBlockedByLive, deployBlockReason, equipLocked } =
    deriveDeployGate({ liveBattles, agent, concurrencyEnabled: concurrencyOn });
  const recentCompleted = useRecentCompletedAgentBattles(3);
  // null while the flag is dark, which is what keeps flag-OFF byte-identical:
  // the slots below receive nothing and render exactly what they rendered.
  // F-1: the Desk describes the BaggerBomb battle, BY TYPE — never by index.
  // Framework §3.1 scopes the Command Center to BaggerBomb, and a ranked battle
  // never drives the Desk in Pass 1. Index selection could not honour that:
  // sortLiveBattles puts ranked FIRST, so orderedLiveBattles[0] picked the
  // ranked battle exactly when both were live. If no BaggerBomb battle is live,
  // there is no Desk — hasLiveBaggerBomb is the render gate.
  const deskBattle = hasLiveBaggerBomb(liveBattles) ? findLiveBaggerBomb(liveBattles) : null;
  const sync = useCommandCenterSync(deskBattle, voiceLayerCaches[deskBattle?.id], agent);
  // Loop rail. Kept on isLive by design: it marks the FURTHEST beat the daily loop has
  // reached (a concurrent BaggerBomb stays deployable beside a live ranked battle, but
  // the furthest beat is still Manage). (Phase 1.5 deliberate disposition; mirrors mobile.)
  const activeStage = isLive ? 'manage' : 'read';

  const [deploying, setDeploying] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const deployDisabled = deploying || deployBlockedByLive || !agent;

  // ── Deploy Ceremony (flag-gated) — mirrors the mobile shell (ruling #2). ────
  const ceremonyOn = isDeployCeremonyOn();
  const starfieldOn = isStarfieldOn();
  const [ceremonyOpen, setCeremonyOpen] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [ceremonyRun, setCeremonyRun] = useState(0); // bump remounts the ceremony (retry)
  // The DEPLOY TARGET's agentId, reported up from deployAgent as soon as it
  // resolves (before the POST). On the casual-clone path this is the clone's id,
  // NOT agent.id — and it is the document the ceremony must watch for progress,
  // since the server writes deployProgress there. Never derived here: only
  // deployAgent knows whether the clone resolved or the fallback fired.
  const [deployTargetId, setDeployTargetId] = useState(null);
  // R1: the same id, readable when the deploy returns (the state above is a
  // render behind it), for the ceremony-off existing-battle open.
  const deployTargetRef = useRef(null);
  const reportDeployTarget = (id) => { deployTargetRef.current = id; setDeployTargetId(id); };
  // Pilot P1c (founder ruling E1): a due idea gets ONE screen before any deploy
  // call, and every button on it deploys — so handleDeploy waits for the choice
  // and still returns the deploy's own result. The gate ref keeps a second press
  // from starting a second check; a ceremony retry re-runs the same deploy
  // without asking again (the choice was made).
  const [dueIdea, setDueIdea] = useState(null); // { due, proceed }
  const ideaGateRef = useRef(false);
  const skipIdeaCheckRef = useRef(false);
  // The waiting deploy's settle (the screen's choice, or 'unmounted'), and deployDisabled as it stands after it.
  const settleChoiceRef = useRef(null);
  const deployDisabledRef = useRef(deployDisabled);
  deployDisabledRef.current = deployDisabled;
  // Unmounted while the screen waits (App swaps the shells at 768 px): settle the waiting deploy, so the
  // app-wide hold is released at once (P1c review L1-2).
  useEffect(() => () => { settleChoiceRef.current?.('unmounted'); }, []);

  const handleDeploy = async () => {
    if (deployDisabled) return { success: false };
    if (skipIdeaCheckRef.current) {
      skipIdeaCheckRef.current = false;
      // t0 for the stage-duration instrumentation — mirrors the mobile shell.
      ceremonyTiming.startRun();
    } else {
      if (ideaGateRef.current) return { success: false };
      ideaGateRef.current = true;
      // t0 includes the idea check (bounded in dueIdeaCheck.js) — mirrors the mobile shell.
      ceremonyTiming.startRun();
      try {
        const due = await checkDueIdea(agent);
        if (due) {
          const choice = await new Promise((settle) => {
            settleChoiceRef.current = settle;
            setDueIdea({ due, proceed: () => settle('proceed') });
          });
          // Mirrors the mobile shell (P1c review L1-2, L4-13).
          if (choice !== 'proceed' || deployDisabledRef.current) return { success: false };
          ceremonyTiming.startRun();
        }
      } finally {
        settleChoiceRef.current = null;
        ideaGateRef.current = false;
        setDueIdea(null);
      }
    }
    if (ceremonyOn) { setCeremonyOpen(true); setDeployResult({ status: 'pending' }); }
    setDeploying(true);
    let result = { success: false };
    try {
      result = await deployAgent(agent.id, onCreateAgentBattle, reportDeployTarget);
    } catch (err) {
      console.error('[Deploy] Error:', err);
      // `postIssued: false` is now TRUE of every throw that can reach here.
      // deployAgent's only remaining throw site is getIdToken(), which runs before
      // the POST; the battle-creation handoff — which runs AFTER a 200 and used to
      // arrive here mislabelled as "never reached the server" — is caught inside
      // deployAgent and returns its battle id instead. Anything unforeseen still
      // lands on the conservative pair, which fails toward "lost contact".
      if (ceremonyOn) result = { success: false, error: err?.message, postIssued: false, httpStatus: null };
    }
    setDeploying(false);
    if (ceremonyOn) {
      // deployResult.js: `existing` for the agent's running battle (R1); every
      // other outcome maps exactly as it did inline here before.
      // Founder ruling Q2: a deploy orphaned by an account change shows the former
      // owner's deploy nothing — the ceremony closes rather than verify or offer
      // Try again under the new account.
      if (isOrphanedResult(result)) setCeremonyOpen(false);
      else setDeployResult(deployResultFor(result));
    } else if (isExistingBattleResult(result)) {
      // R1 (ruling R-R1-2): with the ceremony off, the running game opens
      // directly — the owned server document the server named, never a creation,
      // and no "deployed" toast, because nothing was deployed.
      await openExistingBattleDirectly({
        existingBattleId: result.existingBattleId,
        targetAgentId: deployTargetRef.current,
        liveBattles,
        onOpen: onOpenAgentBattle,
      });
    }
    return result;
  };
  // Remount ONLY if the deploy will actually re-run. handleDeploy bails on
  // deployDisabled before it resets deployResult, so bumping the key regardless
  // mounts a FRESH stage machine onto the PREVIOUS run's `{ status: 'error' }` —
  // which then verifies and authors a terminal claim about a deploy that never
  // happened. deployBlockedByLive flips true exactly when the durable battle from
  // the failure this ceremony exists for lands in the live-battle subscription,
  // so this is the reachable case, not a hypothetical one.
  const handleCeremonyRetry = () => {
    if (deployDisabled) return;
    setCeremonyRun((r) => r + 1);
    skipIdeaCheckRef.current = true; // Pilot P1c: the idea choice was already made for this deploy
    handleDeploy();
  };
  const openFilmRoom = (battle) => { setCurrentBattle?.(battle); setScreen?.('filmRoom'); };
  const openAgentRecord = () => setRecordOpen(true);

  const colScroll = { minHeight: 0, overflowY: 'auto', overflowX: 'hidden' };

  return (
    <div className="cmd-desk-root" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: starfieldOn ? 'transparent' : CMD.bg, color: CMD.ink, position: 'relative', zIndex: 1 }}>
      {/* Responsive (spec D2): ≥1200 = 3-col, fixed viewport height, columns scroll
          independently. 769–1199 = 2-col reflow — left identity rail kept; the
          right lifecycle (Manage 04 / Review 05) folds beneath the center spine;
          the surface page-scrolls. ≤768 never reaches here (App.jsx serves the
          mobile CommandDashboard). */}
      <style>{`
        .cmd-desk-col::-webkit-scrollbar { width: 0; height: 0; }
        .cmd-desk-col { scrollbar-width: none; }
        @media (max-width: 1199px) {
          .cmd-desk-root { height: auto !important; overflow: visible !important; }
          .cmd-desk-grid {
            flex: none !important;
            grid-template-columns: minmax(220px, 280px) minmax(0, 1fr) !important;
            grid-template-areas: "identity center" "identity lifecycle" !important;
            align-items: start !important;
            gap: 16px !important;
            padding: 20px 20px 24px !important;
          }
          .cmd-desk-col { overflow: visible !important; }
          .cmd-desk-topbar { flex-wrap: wrap !important; padding: 16px 20px 14px !important; }
          .cmd-desk-greet, .cmd-desk-wins { min-width: 0 !important; }
          .cmd-desk-wins { order: 2 !important; }
          .cmd-desk-railwrap { order: 3 !important; flex-basis: 100% !important; margin-top: 12px !important; }
          .cmd-loop-pill { padding: 6px 9px !important; gap: 6px !important; }
          .cmd-loop-label { font-size: 9px !important; letter-spacing: 0.08em !important; }
          .cmd-loop-conn { width: 16px !important; margin: 0 3px !important; }
        }
      `}</style>


      {/* top bar — at ≤1199 the rail folds to its own centered row (see <style>) */}
      <div className="cmd-desk-topbar" style={{
        flexShrink: 0, padding: '20px 30px 18px', borderBottom: `1px solid ${CMD.hair}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
      }}>
        <div className="cmd-desk-greet" style={{ minWidth: 250 }}>
          <Eyebrow color={CMD.ink3}>{getGreeting()}, {user?.username || 'Director'}</Eyebrow>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 2, color: CMD.ink }}>Command Center</div>
        </div>
        <div className="cmd-desk-railwrap" style={{ display: 'flex', justifyContent: 'center', flex: '0 1 auto' }}>
          <DeskLoopRail active={activeStage} primary={accent} />
        </div>
        <div className="cmd-desk-wins" style={{
          display: 'flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 999,
          background: CMD.surface, border: `1px solid ${CMD.hair}`, minWidth: 250, justifyContent: 'flex-end',
        }}>
          <Trophy size={15} color={CMD.gold} />
          <Mono style={{ fontSize: 13, color: CMD.ink, fontWeight: 600 }}>{wins}</Mono>
          <span style={{ fontSize: 12, color: CMD.ink3 }}>wins</span>
        </div>
      </div>

      {/* body — three columns */}
      <div className="cmd-desk-grid" style={{
        flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '300px 1fr 358px',
        gridTemplateAreas: '"identity center lifecycle"', gap: 22, padding: '22px 30px 26px',
      }}>
        {/* LEFT — identity */}
        <div className="cmd-desk-col" style={{ ...colScroll, gridArea: 'identity' }}>
          <IdentityPanel
            agent={agent}
            accent={accent}
            live={isLive}
            record={record}
            winRate={winRate}
            levelConfig={levelConfig}
            nextLevelInfo={nextLevelInfo}
            onOpenRecord={openAgentRecord}
          />
        </div>

        {/* CENTER — the act-now spine: Read → Equip → Deploy */}
        <div className="cmd-desk-col" style={{ ...colScroll, gridArea: 'center', display: 'flex', flexDirection: 'column', gap: 22 }}>
          <ReadColumn
            accent={accent}
            agentName={agentName}
            onOpenAgentRecord={openAgentRecord}
            onDeploy={handleDeploy}
            deployDisabled={deployDisabled}
            deploying={deploying}
            isLive={deployBlockedByLive}
            blockReason={deployBlockReason}
            boardEnabled={SCOUTING_BOARD_ENABLED}
            onSeeEyeing={() => setBoardOpen(true)}
          />
          <div id="cmd-desk-equip">
            <EquipBench agent={agent} accent={accent} setShowForge={setShowForge} isLive={equipLocked} />
          </div>
          {/* Deploy card shows while a BaggerBomb can start (flag-off === !isLive,
              byte-identical); flag-on it stays available beside a live ranked battle. */}
          {!deployBlockedByLive && (
            <div style={{ marginTop: 'auto' }}>
              <SectionLabel n="03" label="Deploy" color={accent} />
              <DeployCard
                agent={agent}
                accent={accent}
                deploying={deploying}
                onDeploy={handleDeploy}
                agentName={agentName}
                deployText={deployText}
              />
            </div>
          )}
        </div>

        {/* RIGHT — the battle lifecycle (folds beneath center at ≤1199) */}
        <div className="cmd-desk-col" style={{ ...colScroll, gridArea: 'lifecycle', display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div>
            {/* Command Center Sync (Pass 1): the Agent Desk. `sync` is null while
                the flag is dark, and AgentDesk returns null for a null sync, so
                flag-OFF this renders nothing and the column is unchanged. */}
            {sync && <AgentDesk sync={sync} accent={accent} />}
            <SectionLabel n="04" label={isLive ? 'Manage · live' : 'Manage'} color={isLive ? accent : CMD.ink3} />
            {/* Flag-ON: every live battle, each labeled by type, deterministically
                ordered — no unsorted liveBattles[0] (acceptance #4). Flag-OFF: the
                single legacy card, byte-identical. */}
            {!isLive ? (
              <IdleBlock icon={<Activity size={18} color={CMD.ink3} />} title="No battle live" sub="Deploy to send your agent in" />
            ) : concurrencyOn ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                {orderedLiveBattles.map((b) => (
                  <ManageStation key={b.id} battle={b} showType agent={agent} accent={accent} onOpen={onOpenAgentBattle} sync={syncForBattle(sync, b.id)} />
                ))}
              </div>
            ) : (
              <ManageStation battle={liveBattle} agent={agent} accent={accent} onOpen={onOpenAgentBattle} />
            )}
          </div>

          <div>
            <SectionLabel n="05" label={recentCompleted.length > 0 ? 'Review · last battle' : 'Review'} color={recentCompleted.length > 0 ? accent : CMD.ink3} />
            {recentCompleted.length > 0 ? (
              <ReviewStation battles={recentCompleted} agent={agent} accent={accent} onReview={openFilmRoom} />
            ) : (
              <IdleBlock icon={<Award size={18} color={CMD.ink3} />} title="No battles yet" sub="Your first grade unlocks here" />
            )}
          </div>

          <div style={{ marginTop: 'auto', textAlign: 'center', paddingTop: 6 }}>
            <Mono style={{ fontSize: 9, letterSpacing: '0.16em', color: CMD.ink3, textTransform: 'uppercase' }}>The loop closes · Review sharpens tomorrow’s Read</Mono>
          </div>
        </div>
      </div>

      <AgentRecordSheet
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        agent={agent}
        loading={agentLoading}
        accent={accent}
        levelConfig={levelConfig}
        nextLevelInfo={nextLevelInfo}
        masteryProfile={masteryProfile}
        dock="center"
      />

      {SCOUTING_BOARD_ENABLED && (
        <ScoutingBoardSheet
          open={boardOpen}
          onClose={() => setBoardOpen(false)}
          dock="center"
          agent={agent}
          accent={accent}
          deploying={deploying}
          deployDisabled={deployDisabled}
          isLive={deployBlockedByLive}
          onDeploy={handleDeploy}
        />
      )}

      {dueIdea && <DueIdeaInterstitial due={dueIdea.due} accent={accent} onProceed={dueIdea.proceed} />}

      {ceremonyOn && ceremonyOpen && (
        <DeployCeremony
          key={ceremonyRun}
          agent={agent}
          accent={accent}
          agentName={agentName}
          directiveCount={activeDirectives?.length || 0}
          deployResult={deployResult}
          targetAgentId={deployTargetId}
          onEnterBattle={(recoveredBattle) => { onEnterBattle?.(recoveredBattle); setCeremonyOpen(false); }}
          // R1: the existing-battle terminal opens the owned server document.
          liveBattles={liveBattles}
          onOpenBattle={(battle) => { onOpenAgentBattle?.(battle); setCeremonyOpen(false); }}
          onDismiss={() => setCeremonyOpen(false)}
          onRetry={handleCeremonyRetry}
        />
      )}
    </div>
  );
}
